import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FiChevronDown, FiPlus } from 'react-icons/fi';
import { DialogBox } from "../components/Shared";
import "./Dashboard.css";
import { solutionTypes, configuredSolutions as defaultSolutions, getSolutionTypeInfo } from "../Data/Data";

const Dashboard = () => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // Solution management state
  const [configuredSolutions, setConfiguredSolutions] = useState(defaultSolutions);
  const [selectedSolution, setSelectedSolution] = useState(() => {
    // Find default solution or use first one
    const defaultSol = defaultSolutions.find(s => s.isDefault);
    return defaultSol ? defaultSol.id : defaultSolutions[0]?.id;
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newSolutionType, setNewSolutionType] = useState('');
  const [newSolutionName, setNewSolutionName] = useState('');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Navigate to default solution on mount
  useEffect(() => {
    const currentSolution = configuredSolutions.find(s => s.id === selectedSolution);
    if (currentSolution) {
      const typeInfo = getSolutionTypeInfo(currentSolution.type);
      if (typeInfo && typeInfo.route) {
        navigate(typeInfo.route, { replace: true });
      }
    }
  }, []);

  const handleSolutionSelect = (solutionId) => {
    setSelectedSolution(solutionId);
    setIsDropdownOpen(false);

    // Navigate to the selected solution's dashboard
    const solution = configuredSolutions.find(s => s.id === solutionId);
    if (solution) {
      const typeInfo = getSolutionTypeInfo(solution.type);
      if (typeInfo && typeInfo.route) {
        navigate(typeInfo.route);
      }
    }
  };

  const handleAddSolution = () => {
    if (newSolutionType && newSolutionName.trim()) {
      const newSolution = {
        id: `${newSolutionType}-${Date.now()}`,
        type: newSolutionType,
        name: newSolutionName.trim(),
        isDefault: false
      };
      const updatedSolutions = [...configuredSolutions, newSolution];
      setConfiguredSolutions(updatedSolutions);
      setSelectedSolution(newSolution.id);
      setIsAddDialogOpen(false);
      setNewSolutionType('');
      setNewSolutionName('');

      // Navigate to the new solution's dashboard
      const typeInfo = getSolutionTypeInfo(newSolution.type);
      if (typeInfo && typeInfo.route) {
        navigate(typeInfo.route);
      }
    }
  };

  const currentSolution = configuredSolutions.find(s => s.id === selectedSolution);
  const currentTypeInfo = currentSolution ? getSolutionTypeInfo(currentSolution.type) : null;

  return (
    <div className="env-dashboard">
      {/* Header with Solution Selector */}
      <div className="env-dashboard-header">
        <div className="env-dashboard-header-content">
          <div className="env-dashboard-header-left">
            <h1 className="env-dashboard-title">
              {currentSolution ? currentSolution.name : 'Dashboard'}
            </h1>
            <p className="env-dashboard-subtitle">
              {currentTypeInfo ? currentTypeInfo.fullName : 'Select a solution to monitor'}
            </p>
          </div>

          {/* Solution Dropdown */}
          <div className="solution-dropdown-container" ref={dropdownRef}>
            <button
              className="solution-dropdown-trigger"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              {currentTypeInfo && (
                <currentTypeInfo.icon size={20} color={currentTypeInfo.color} />
              )}
              <span>{currentSolution ? currentSolution.name : 'Select Solution'}</span>
              <FiChevronDown
                size={18}
                className={`dropdown-chevron ${isDropdownOpen ? 'open' : ''}`}
              />
            </button>

            {isDropdownOpen && (
              <div className="solution-dropdown-menu">
                <div className="dropdown-section-label">Configured Solutions</div>
                {configuredSolutions.map((solution) => {
                  const typeInfo = getSolutionTypeInfo(solution.type);
                  return (
                    <button
                      key={solution.id}
                      className={`dropdown-item ${selectedSolution === solution.id ? 'active' : ''}`}
                      onClick={() => handleSolutionSelect(solution.id)}
                    >
                      {typeInfo && <typeInfo.icon size={18} color={typeInfo.color} />}
                      <span>{solution.name}</span>
                      {solution.isDefault && <span className="default-badge">Default</span>}
                    </button>
                  );
                })}
                <div className="dropdown-divider" />
                <button
                  className="dropdown-item add-solution-btn"
                  onClick={() => {
                    setIsDropdownOpen(false);
                    setIsAddDialogOpen(true);
                  }}
                >
                  <FiPlus size={18} />
                  <span>Add New Solution</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Solution Dialog */}
      <DialogBox
        isOpen={isAddDialogOpen}
        onClose={() => {
          setIsAddDialogOpen(false);
          setNewSolutionType('');
          setNewSolutionName('');
        }}
        title="Add New Solution"
        onSave={handleAddSolution}
        saveLabel="Add Solution"
        cancelLabel="Cancel"
      >
        <div className="add-solution-form">
          <div className="form-group">
            <label>Solution Type</label>
            <div className="solution-type-grid">
              {solutionTypes.map((type) => (
                <button
                  key={type.id}
                  className={`solution-type-option ${newSolutionType === type.id ? 'selected' : ''}`}
                  onClick={() => {
                    setNewSolutionType(type.id);
                    if (!newSolutionName) {
                      setNewSolutionName(`${type.name} Dashboard`);
                    }
                  }}
                  type="button"
                >
                  <type.icon size={24} color={type.color} />
                  <span>{type.name}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label>Solution Name</label>
            <input
              type="text"
              className="dialog-input"
              value={newSolutionName}
              onChange={(e) => setNewSolutionName(e.target.value)}
              placeholder="Enter solution name"
            />
          </div>
        </div>
      </DialogBox>
    </div>
  );
};

export default Dashboard;
